/**
 * Menu Helper Utilities
 * Functions for determining menu eligibility for RSVP and admin override operations
 */

import { fromISODate, isSunday, isPastDate, addDays, toISODate } from './dateHelpers';
import type { Menu } from '@/types/api';

/**
 * Check if a menu is eligible for member RSVP
 * - Must be published
 * - Must be a regular menu (not announcement)
 * - Cannot be on Sunday
 * - Cannot be in the past unless an active custom cutoff override keeps it open
 * - Cutoff must NOT have passed
 */
export function isMenuRSVPEligible(menu: Menu): boolean {
  const menuDate = fromISODate(menu.date);
  return (
    menu.status === 'PUBLISHED' &&
    menu.type === 'MENU' &&
    !isSunday(menuDate) &&
    !isMenuPastForMemberRSVP(menu) &&
    !menu.rsvpCutoffPassed
  );
}

export function hasActiveRSVPCutoffOverride(
  menu: Menu,
  cutoffPassed = !!menu.rsvpCutoffPassed
): boolean {
  return !!menu.rsvpCutoffAt && !cutoffPassed;
}

export function isMenuPastForMemberRSVP(
  menu: Menu,
  cutoffPassed = !!menu.rsvpCutoffPassed
): boolean {
  return isPastDate(fromISODate(menu.date)) && !hasActiveRSVPCutoffOverride(menu, cutoffPassed);
}

/**
 * Check if a menu is eligible for admin RSVP override
 * - Must be published
 * - Must be a regular menu (not announcement)
 * - Cannot be on Sunday
 * - Cannot be in the past
 * - Cutoff MUST have passed (that's the point of overrides)
 */
export function isMenuOverrideEligible(menu: Menu): boolean {
  const menuDate = fromISODate(menu.date);
  return (
    menu.status === 'PUBLISHED' &&
    menu.type === 'MENU' &&
    !isSunday(menuDate) &&
    !isPastDate(menuDate) &&
    !!menu.rsvpCutoffPassed
  );
}

/**
 * Check if a menu is disabled for RSVP interaction
 * This is the inverse of eligibility - used for UI disabled states
 */
export function isMenuRSVPDisabled(menu: Menu): boolean {
  return !isMenuRSVPEligible(menu);
}

/**
 * Day labels for cutoff day display
 * Index corresponds to day of week (0 = Sunday, 6 = Saturday)
 */
export const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/**
 * Get the day label for a cutoff day number
 */
export function getCutoffDayLabel(cutoffDay: number): string | null {
  return DAY_LABELS[cutoffDay] ?? null;
}

/**
 * Filter the given week-start Mondays down to those with at least one menu (within the week)
 * whose RSVP cutoff has passed. Caller is responsible for providing unique, ordered week starts.
 */
export function findPassedCutoffWeeks(
  weekStarts: Date[],
  menusByDate: Map<string, Menu>
): Date[] {
  return weekStarts.filter((start) => {
    for (let i = 0; i < 7; i++) {
      const day = toISODate(addDays(start, i));
      if (menusByDate.get(day)?.rsvpCutoffPassed) return true;
    }
    return false;
  });
}
